import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import toast from "react-hot-toast";

/**
 * Custom hook to handle smart back button navigation
 * For mobile: Handles bottom tab navigation with smart logic
 * For web: Browser back/swipe works normally with React Router
 */
export const useBackButtonHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const backPressCountRef = useRef(0);
  const backPressTimerRef = useRef(null);

  // Define bottom navigation tab routes
  const bottomTabRoutes = ["/", "/orders", "/cart", "/profile"];

  useEffect(() => {
    // Reset back press count
    const resetBackPressCount = () => {
      backPressCountRef.current = 0;
      if (backPressTimerRef.current) {
        clearTimeout(backPressTimerRef.current);
        backPressTimerRef.current = null;
      }
    };

    // Check if current path is a bottom tab route
    const isBottomTabRoute = (path) => {
      return bottomTabRoutes.includes(path);
    };

    // Reset counter on navigation
    resetBackPressCount();

    // Only register hardware back button listener on native platforms
    if (!Capacitor.isNativePlatform()) {
      // On web, browser back/swipe works automatically with React Router
      return () => {
        if (backPressTimerRef.current) {
          clearTimeout(backPressTimerRef.current);
        }
      };
    }

    // Handle hardware back button on Android (Capacitor)
    const handleBackButton = CapacitorApp.addListener(
      "backButton",
      ({ canGoBack }) => {
        const currentPath = location.pathname;
        const isCurrentPathTab = isBottomTabRoute(currentPath);
        const isHomePage = currentPath === "/";

        // Case 1: User is on home page
        if (isHomePage) {
          backPressCountRef.current += 1;

          if (backPressCountRef.current === 1) {
            // First press - show toast and start timer
            toast("Press back again to exit", {
              duration: 2000,
              icon: "👋",
            });

            backPressTimerRef.current = setTimeout(() => {
              backPressCountRef.current = 0;
            }, 2000);
          } else if (backPressCountRef.current >= 2) {
            // Second press - minimize app
            CapacitorApp.minimizeApp();
            resetBackPressCount();
          }
          return;
        }

        // Case 2: User is on another bottom tab (not home)
        if (isCurrentPathTab && !isHomePage) {
          // Navigate to home first
          navigate("/", { replace: true });
          resetBackPressCount();
          return;
        }

        // Case 3: User is on a deep page (not a bottom tab)
        if (canGoBack) {
          // Navigate back normally
          navigate(-1);
          resetBackPressCount();
        } else {
          // No history, go to home
          navigate("/", { replace: true });
          resetBackPressCount();
        }
      }
    );

    // Cleanup
    return () => {
      handleBackButton.remove();
      if (backPressTimerRef.current) {
        clearTimeout(backPressTimerRef.current);
      }
    };
  }, [location.pathname, navigate]);
};
